/**
 * Alert — inline status/error message.
 *
 * Use for non-blocking, page- or form-level feedback (errors, banners, info
 * notices). Not a toast — Alert stays in the document flow. Not a modal —
 * Alert doesn't trap focus or block interaction.
 *
 * Variants map to semantic tokens:
 *   info    — neutral info notice
 *   success — affirmative confirmation
 *   warning — needs-your-attention notice
 *   error   — failure / validation summary
 *
 * Role defaults to `role="alert"` for error/warning and `role="status"` for
 * info/success (the assertive vs polite live-region convention). Pass `role`
 * to override.
 *
 * Pass `onDismiss` to render a trailing × close button.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { t } from '@/lib/i18n';
import styles from './Alert.module.css';

type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  /** Optional title rendered as a bold first line. */
  title?: ReactNode;
  /** When provided, renders a trailing × button that calls this on click. */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button. Defaults to "Dismiss". */
  dismissLabel?: string;
}

const VARIANT_CLASS: Record<AlertVariant, string | undefined> = {
  info: styles.variantInfo,
  success: styles.variantSuccess,
  warning: styles.variantWarning,
  error: styles.variantError,
};

const DEFAULT_ROLE: Record<AlertVariant, 'alert' | 'status'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  error: 'alert',
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { variant = 'info', title, role, onDismiss, dismissLabel, className, children, ...rest },
  ref,
) {
  const composedClassName = [styles.alert, VARIANT_CLASS[variant], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={composedClassName} role={role ?? DEFAULT_ROLE[variant]} {...rest}>
      <div className={styles.content}>
        {title ? <div className={styles.title}>{title}</div> : null}
        {children ? <div className={styles.body}>{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label={dismissLabel ?? t('Dismiss')}
        >
          <span aria-hidden="true">×</span>
        </button>
      ) : null}
    </div>
  );
});
